import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Form,
  Input,
  Select,
  Row,
  Col,
  Upload,
  Modal,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  SearchOutlined,
  ReloadOutlined,
  UploadOutlined,
  DownloadOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import PageHeader from '../../components/common/PageHeader';
import FilterPanel from '../../components/common/FilterPanel';
import {
  PromotionLocationItem,
  PromotionLocationItemFilters,
  PromotionLocationItemImportData,
} from '../../types/promotionLocationItem';
import { Item } from '../../types/item';
import promotionLocationItemService from '../../services/promotionLocationItemService';
import { itemService } from '../../services/itemService';
import { formatTimestamp } from '../../utils/responseTransformer';
import '../../styles/promotionLocationItem.css';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { Option } = Select;

interface PromotionLocationItemListProps {
  // 可以添加额外的props
}

const PromotionLocationItemList: React.FC<PromotionLocationItemListProps> = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  // 时间格式化辅助函数
  const formatDateTime = (timestamp: number): string => {
    if (!timestamp) {
      return '-';
    }
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };
  
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<PromotionLocationItem[]>([]);
  const [itemOptions, setItemOptions] = useState<Item[]>([]);
  const [itemOptionsLoading, setItemOptionsLoading] = useState(false);
  const [filters, setFilters] = useState<PromotionLocationItemFilters>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // Excel导入相关状态
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importResult, setImportResult] = useState<any>(null);

  // 获取数据
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await promotionLocationItemService.pageQueryPromotionLocationItems({
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        ...filters,
      });

      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(prev => ({
          ...prev,
          total: response.data!.paging.totalCount,
        }));
      } else {
        message.error(response.errorMessage || t('promotionLocationItem.queryFailed'));
      }
    } catch (error) {
      console.error('获取促销位货品列表失败:', error);
      message.error(t('promotionLocationItem.queryFailed'));
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  // 获取商品选项
  const fetchItemOptions = useCallback(async () => {
    setItemOptionsLoading(true);
    try {
      const response = await itemService.listItems({});
      if (response.success && response.data) {
        setItemOptions(response.data);
      }
    } catch (error) {
      console.error('获取商品选项失败:', error);
    } finally {
      setItemOptionsLoading(false);
    }
  }, []);

  // 初始化数据
  useEffect(() => {
    fetchData();
  }, [fetchData, fetchItemOptions]);

  // 处理分页
  const handleTableChange = (page: number, size?: number) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: size || prev.pageSize,
    }));
  };

  // 处理过滤器变更
  const handleFilterChange = (values: any) => {
    const newFilters: PromotionLocationItemFilters = {
      locationCode: values.locationCode?.trim() || undefined,
      itemCode: values.itemCode || undefined,
      ownerCode: values.ownerCode?.trim() || undefined,
      search: values.search?.trim() || undefined,
    };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 })); // 重置到第一页
  };

  // 处理重置
  const handleResetFilters = () => {
    form.resetFields();
    setFilters({});
    setPagination(prev => ({ ...prev, current: 1 })); // 重置到第一页
  };


  // 下载导入模板
  const handleDownloadTemplate = async () => {
    try {
      const response = await promotionLocationItemService.downloadImportTemplate();
      const link = document.createElement('a');
      link.href = response.data.downloadUrl;
      link.download = response.data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    
      message.success(t('promotionLocationItem.templateDownloadSuccess'));
    } catch (error) {
      console.error('下载模板失败:', error);
      message.error(t('promotionLocationItem.templateDownloadFailed'));
    }
  };

  // Excel导入配置
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    accept: '.xlsx,.xls,.csv',
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel' ||
                     file.type === 'text/csv';
      if (!isExcel) {
        message.error(t('promotionLocationItem.onlyExcelAllowed'));
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t('promotionLocationItem.fileSizeLimit'));
        return false;
      }
      setFileList([file]);
      return false; // 阻止自动上传
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  // 处理Excel导入
  const handleImport = async () => {
    if (fileList.length === 0) {
      message.error(t('promotionLocationItem.pleaseSelectFile'));
      return;
    }

    setImportLoading(true);

    try {
      const file = fileList[0];
      message.loading(t('promotionLocationItem.importing'), 0);
      
      // 这里应该实际读取文件内容，但在当前实现中我们保持原样

      const response = await promotionLocationItemService.importPromotionLocationItems(file);
      message.destroy();
      
      if (response.success) {
        setImportModalVisible(false);
          //刷新列表数据
          fetchData();
      } else {
        message.error(response.errorMessage || t('promotionLocationItem.importFailed'));
      }
    } catch (error) {
      message.destroy();
      console.error('Excel导入失败:', error);
      message.error(t('promotionLocationItem.importFailed'));
    } finally {
      setImportLoading(false);
    }
  };

  // 表格列定义
  const columns: ColumnsType<PromotionLocationItem> = [
    {
      title: t('promotionLocationItem.locationCode'),
      dataIndex: 'locationCode',
      key: 'locationCode',
      width: 150,
      sorter: true,
    },
    {
      title: t('promotionLocationItem.warehouseCode'),
      dataIndex: 'warehouseCode',
      key: 'warehouseCode',
      width: 150,
    },
    {
      title: t('promotionLocationItem.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 150,
    },
    {
      title: t('promotionLocationItem.itemName'),
      dataIndex: 'itemDTO.name',
      key: 'itemDTO.name',
      width: 200,
      ellipsis: true,
      render: (_, record) => record.itemDTO?.name || record.itemName || '',
    },
    {
      title: t('promotionLocationItem.ownerCode'),
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      width: 150,
    },
    {
      title: t('promotionLocationItem.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('promotionLocationItem.updateTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
  ];

  return (
    <div>
      <FilterPanel
      title={t('menu.promotionLocationItem')}
       onFilter={handleFilterChange}
        onReset={handleResetFilters}
        >
        <Col span={12}>
          <Form.Item name="locationCode" label={t('promotionLocationItem.filter.locationCode')}>
            <Input
              placeholder={t('promotionLocationItem.locationCodePlaceholder')}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="itemCode" label={t('promotionLocationItem.filter.item')}>
            <Select
              placeholder={t('promotionLocationItem.itemPlaceholder')}
              allowClear
              showSearch
              loading={itemOptionsLoading}
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
              onDropdownVisibleChange={async (open) => {
                // 只在下拉框首次打开时加载数据
                if (open && itemOptions.length === 0 && !itemOptionsLoading) {
                  await fetchItemOptions();
                }
              }}
            >
              {itemOptions.map(item => (
                <Option key={item.code} value={item.code}>
                  {item.code} - {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              {t('promotionLocationItem.downloadTemplate')}
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setImportModalVisible(true)}
            >
              {t('promotionLocationItem.excelImport')}
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('promotionLocationItem.paginationTotal', { start: range[0], end: range[1], total }),
            onChange: handleTableChange,
          }}
          scroll={{ x: 1000, y: 500 }}
        />
      </Card>

      {/* Excel导入弹窗 */}
      <Modal
        title={t('promotionLocationItem.importModalTitle')}
        open={importModalVisible}
        onOk={handleImport}
        onCancel={() => {
          if (!importLoading) {
            setImportModalVisible(false);
            setFileList([]);
            setImportResult(null);
          }
        }}
        confirmLoading={importLoading}
        width={600}
        maskClosable={!importLoading}
      >
        {!importResult ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text>
                {t('promotionLocationItem.uploadInstruction')}
              </Text>
            </div>
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">{t('promotionLocationItem.selectFile')}</p>
              <p className="ant-upload-hint">
                {t('promotionLocationItem.importHint')}
              </p>
            </Upload.Dragger>
          </div>
        ) : (
          <div>
            <Title level={4}>
              {t('promotionLocationItem.importComplete')}
            </Title>
            <div style={{ marginBottom: 16 }}>
              <Text>{t('promotionLocationItem.importSuccessCount', { count: importResult.successCount })}</Text>
              <br />
              <Text>{t('promotionLocationItem.importFailureCount', { count: importResult.failureCount })}</Text>
            </div>
            {importResult.errorDetails && importResult.errorDetails.length > 0 && (
              <div>
                <Text strong>{t('promotionLocationItem.errorDetails')}</Text>
                <ul>
                  {importResult.errorDetails.map((error: string, index: number) => (
                    <li key={index}>
                      <Text type="danger">{error}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PromotionLocationItemList;